import React, { useState, useEffect } from 'react';
import { createUser, getUsers, updateUser, deleteUserFunction, updateUserPassword } from '../userFunctions';
import { HiOutlineTrash, HiOutlinePencil, HiPlus, HiSearch, HiSortAscending, HiSortDescending, HiEye, HiEyeOff } from 'react-icons/hi';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebaseconfig';
import '../css/area.css';
import Swal from 'sweetalert2';

export default function UserControlPage() {
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    area: '',
    user: ''
  });
  const [areas, setAreas] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [filteredArea, setFilteredArea] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatedPassword, setRepeatedPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    passwordConfirmation: false
  });
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchAreas();
  }, []);

  const fetchUsers = async () => {
    const result = await getUsers();
    if (result.success) {
      setUsers(result.users);
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: result.message,
      });
    }
  };

  const fetchAreas = async () => {
    try {
      const areasQuery = query(collection(db, 'areacontroller'), orderBy('name', 'asc'));
      const querySnapshot = await getDocs(areasQuery);
      const areasData = querySnapshot.docs.map(doc => doc.data().name);
      setAreas(areasData);
    } catch (error) {
      console.error('Error fetching areas:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al obtener áreas: ' + error.message,
      });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== passwordConfirmation) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    formData.email = formData.email.toLowerCase();
    const result = await createUser(formData);
    if (result.success) {
      Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: result.message,
      });
      setFormData({ name: '', email: '', password: '', area: '', user: '' });
      setPasswordConfirmation('');
      setPasswordError('');
      fetchUsers();
      setShowCreateModal(false);
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: result.message,
      });
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleDelete = async (userId) => {
    console.log('Attempting to delete user with ID:', userId);
    try {
      const result = await deleteUserFunction(userId);
      console.log('Delete user result:', result);
      if (result.success) {
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: result.message,
        });
        await fetchUsers(); // Refresh the user list
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: result.message || 'Error al eliminar usuario',
        });
      }
    } catch (error) {
      console.error('Error in handleDelete:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Error al eliminar usuario: ' + (error.message || 'Unknown error'),
      });
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      area: user.area,
      user: user.user
    });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingUser) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se ha seleccionado ningún usuario para editar',
      });
      return;
    }
    const result = await updateUser(editingUser.id, formData);
    if (result.success) {
      Swal.fire({
        icon: 'success',
        title: 'Éxito',
        text: result.message,
      });
      setShowEditModal(false);
      fetchUsers();
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: result.message,
      });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!editingUser) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se ha seleccionado ningún usuario para cambiar la contraseña',
      });
      return;
    }
    if (newPassword !== repeatedPassword) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Las contraseñas no coinciden',
      });
      return;
    }
    const result = await updateUserPassword(editingUser.id, newPassword);
    if (result.success) {
      Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'La contraseña se ha cambiado correctamente.',
      });
      setShowPasswordModal(false);
      setNewPassword('');
      setRepeatedPassword('');
    } else {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: result.message,
      });
    }
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
    fetchUsers();
  };

  const handleOpenCreateModal = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      area: '',
      user: ''
    });
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      area: '',
      user: ''
    });
    setShowCreateModal(false);
  };  

  const filteredUsers = users.filter(user => 
    (user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
     user.area.toLowerCase().includes(searchTerm.toLowerCase())) &&
    (filteredArea === '' || user.area.toLowerCase() === filteredArea.toLowerCase())
  );

  return (
    <div className="user-control-page">
      <div className="user-management-header">
        <h2>Usuarios Existentes</h2>
        <button onClick={handleOpenCreateModal} className="add-user-btn">
          <HiPlus /> Agregar Usuario
        </button>
      </div>
      <div className="filters">
        <input
          type="text"
          placeholder="Buscar usuarios..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <select
          value={filteredArea}
          onChange={(e) => setFilteredArea(e.target.value)}
          className="area-filter"
        >
          <option value="">Todas las áreas</option>
          {areas.map((area, index) => (
            <option key={index} value={area}>{area}</option>
          ))}
        </select>
      </div>
      <div 
        className="table-container"
        style={{ maxHeight: '400px', overflowY: 'auto' }}
      >
      <table className="users-table">
        <thead className='thead-area'>
          <tr>
            <th onClick={() => handleSort('name')}>
              Nombre {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? <HiSortAscending /> : <HiSortDescending />)}
            </th>
            <th onClick={() => handleSort('user')}>
              Usuario {sortConfig.key === 'user' && (sortConfig.direction === 'asc' ? <HiSortAscending /> : <HiSortDescending />)}
            </th>
            <th onClick={() => handleSort('area')}>
              Área {sortConfig.key === 'area' && (sortConfig.direction === 'asc' ? <HiSortAscending /> : <HiSortDescending />)}
            </th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map(user => (
            <tr key={user.id}>
              <td>{user.name}</td>
              <td>{user.user}</td>
              <td>{user.area}</td>
              <td>
                <button onClick={() => handleEdit(user)} className="edit-btn">
                  <HiOutlinePencil />
                </button>
                <button onClick={() => handleDelete(user.id, user.email)} className="delete-btn">
                  <HiOutlineTrash />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {showCreateModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Crear Nuevo Usuario</h2>
            <form onSubmit={handleSubmit}>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Nombre"
                required
              />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                required
              />
              <div className="password-input-container">
                <input
                  type={showPasswords.password ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Contraseña"
                  required
                />
                <button type="button" onClick={() => togglePasswordVisibility('password')} className="password-toggle-btn">
                  {showPasswords.password ? <HiEyeOff /> : <HiEye />}
                </button>
              </div>
              <div className="password-input-container">
                <input
                  type={showPasswords.passwordConfirmation ? "text" : "password"}
                  name="passwordConfirmation"
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  placeholder="Confirmar Contraseña"
                  required
                />
                <button type="button" onClick={() => togglePasswordVisibility('passwordConfirmation')} className="password-toggle-btn">
                  {showPasswords.passwordConfirmation ? <HiEyeOff /> : <HiEye />}
                </button>
              </div>
              {passwordError && <p className="error-message">{passwordError}</p>}
              <select
                name="area"
                value={formData.area}
                onChange={handleChange}
                required
                style={{ maxHeight: '200px', overflowY: 'auto' }}
              >
                <option value="">Seleccionar Área</option>
                <option value="admin">admin</option>
                {areas.map((area, index) => (
                  <option key={index} value={area}>{area}</option>
                ))}
              </select>
              <input
                type="text"
                name="user"
                value={formData.user}
                onChange={handleChange}
                placeholder="Usuario"
                required
              />
              <button type="submit">Crear Usuario</button>
              <button type="button" onClick={handleCloseCreateModal}>Cancelar</button>
            </form>
          </div>
        </div>
      )}
      {showEditModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Editar Usuario</h2>
            <form onSubmit={handleUpdate}>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Nombre"
                required
              />
              <select
                name="area"
                value={formData.area}
                onChange={handleChange}
                required
                style={{ maxHeight: '200px', overflowY: 'auto' }}
              >
                <option value="">Seleccionar Área</option>
                <option value="admin">admin</option>
                {areas.map((area, index) => (
                  <option key={index} value={area}>{area}</option>
                ))}
              </select>
              <input
                type="text"
                name="user"
                value={formData.user}
                onChange={handleChange}
                placeholder="Usuario"
                required
              />
              <button type="submit">Actualizar Usuario</button>
              <button type="button" className='change-password' onClick={() => setShowPasswordModal(true)}>Cambiar Contraseña</button>
              <button type="button" onClick={() => setShowEditModal(false)}>Cancelar</button>
            </form>
          </div>
        </div>
      )}
      {showPasswordModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>Cambiar Contraseña</h2>
            <form onSubmit={handlePasswordChange}>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nueva Contraseña"
                required
              />
              <input
                type="password"
                value={repeatedPassword}
                onChange={(e) => setRepeatedPassword(e.target.value)}
                placeholder="Repetir Nueva Contraseña"
                required
              />
              <button type="submit">Cambiar Contraseña</button>
              <button type="button" onClick={() => {
                setShowPasswordModal(false);
                setNewPassword('');
                setRepeatedPassword('');
              }}>Cancelar</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

